/**
 * Brouillons API client (TICFE / TICGN / TURPE)
 *
 * Remplace l'ancien stockage `localStorage` par des appels HTTP vers le backend.
 * Permet à un commercial de reprendre un brouillon depuis n'importe quel poste.
 *
 * Inclut une migration silencieuse depuis localStorage (une seule fois par type),
 * pour ne pas perdre les brouillons déjà sauvegardés en local.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

const _legacyKey = (type) => ({
  ticfe: 'brouillons_rapports',
  ticgn: 'brouillons_ticgn',
  turpe: 'brouillons_turpe',
}[type]);

const _migrationFlagKey = (type) => `brouillons_migrated_${type}`;

const _headers = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
});

/**
 * Liste les brouillons visibles par l'utilisateur courant.
 * @param {('ticfe'|'ticgn'|'turpe')} [type] - Filtre par type (optionnel)
 */
export async function listBrouillons(type) {
  const url = type ? `${API_URL}/api/brouillons?type=${type}` : `${API_URL}/api/brouillons`;
  const res = await fetch(url, { headers: _headers() });
  if (!res.ok) return [];
  return res.json();
}

/**
 * Récupère un brouillon par son ID.
 */
export async function getBrouillon(id) {
  const res = await fetch(`${API_URL}/api/brouillons/${id}`, { headers: _headers() });
  if (!res.ok) return null;
  return res.json();
}

/**
 * Crée ou met à jour un brouillon. Le serveur upsert sur (user, type, siren)
 * si pas d'id explicite.
 *
 * @param {Object} brouillon
 * @param {string} brouillon.type - 'ticfe' | 'ticgn' | 'turpe'
 * @param {string} [brouillon.id] - Si fourni, force la mise à jour de ce brouillon
 * @param {string} [brouillon.nom_entreprise]
 * @param {string} [brouillon.siren]
 * @param {number} [brouillon.step]
 * @param {Object} [brouillon.payload] - État sérialisable du wizard
 */
export async function saveBrouillon(brouillon) {
  const res = await fetch(`${API_URL}/api/brouillons`, {
    method: 'POST',
    headers: _headers(),
    body: JSON.stringify(brouillon),
  });
  if (!res.ok) throw new Error(`save failed: ${res.status}`);
  return res.json();
}

/**
 * Supprime un brouillon.
 */
export async function deleteBrouillon(id) {
  const res = await fetch(`${API_URL}/api/brouillons/${id}`, {
    method: 'DELETE',
    headers: _headers(),
  });
  if (!res.ok) throw new Error(`delete failed: ${res.status}`);
  return res.json();
}

/**
 * Migration silencieuse depuis localStorage vers MongoDB.
 * Exécutée une seule fois par type (flag dans localStorage).
 *
 * @param {('ticfe'|'ticgn'|'turpe')} type
 * @param {Function} [extract] - Convertit l'objet legacy en BrouillonIn ({nom_entreprise,siren,step,payload})
 */
export async function migrateLocalStorageBrouillons(type, extract) {
  if (localStorage.getItem(_migrationFlagKey(type)) === '1') return;
  const key = _legacyKey(type);
  if (!key) return;

  let legacy = [];
  try {
    legacy = JSON.parse(localStorage.getItem(key) || '[]');
  } catch {
    legacy = [];
  }
  if (!Array.isArray(legacy) || legacy.length === 0) {
    localStorage.setItem(_migrationFlagKey(type), '1');
    return;
  }

  // Tentative d'upload de chaque brouillon
  for (const b of legacy) {
    try {
      const mapped = typeof extract === 'function'
        ? extract(b)
        : {
            nom_entreprise: b.nom_entreprise || b.formData?.nom_entreprise || '',
            siren: b.siren || b.formData?.siren || '',
            step: b.step || 1,
            payload: b,
          };
      await saveBrouillon({ type, ...mapped });
    } catch (e) {
      console.error('Migration brouillon échouée pour', b?.id, e);
    }
  }

  localStorage.setItem(_migrationFlagKey(type), '1');
  // Nettoyage local après migration réussie
  localStorage.removeItem(key);
}
